import { useMemo } from "react";
import { getUser } from "@synthesis/shared-auth";
import { jwtDecode } from "jwt-decode";

export const GLOBAL_ROLE = {
  EXTERNAL_OPERATOR: "ВНЕШНЕЕ СОГЛАСОВАНИЕ ОПЕРАТОР",
  EXTERNAL_APPLICANT: "ВНЕШНЕЕ СОГЛАСОВАНИЕ ЗАЯВИТЕЛЬ",
  EXTERNAL_WITHOUT_ROLE: "ВНЕШНЕЕ СОГЛАСОВАНИЕ НЕТ РОЛИ",
  INTERNAL_SERVICE_ADMINISTRATOR: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ АДМИНИСТРАТОР УСЛУГ",
  INTERNAL_MONITORING: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ МОНИТОРИНГ",
  INTERNAL_NTP_CHIEF: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ НАЧАЛЬНИК НТП",
  INTERNAL_APPARATUS_NG: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ АППАРАТ НГ",
  INTERNAL_WITHOUT_ROLE: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ НЕТ РОЛИ",
  INTERNAL_OPERATOR: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ ОПЕРАТОР",
  INTERNAL_NTP_RESPONSIBLE: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ ОТВЕТСТВЕННЫЙ НТП",
  INTERNAL_RELATED_DEPARTMENTS: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ ПРИЧАСТНЫЕ ПОДРАЗДЕЛЕНИЯ",
  INTERNAL_NG_SECRETARY: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ СЕКРЕТАРЬ НГ",
  INTERNAL_AGREEING: "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ СОГЛАСУЮЩИЙ",
} as const;

export const GLOBAL_ROLES = Object.fromEntries(Object.values(GLOBAL_ROLE).map((role) => [role, role])) as Record<
  (typeof GLOBAL_ROLE)[keyof typeof GLOBAL_ROLE],
  (typeof GLOBAL_ROLE)[keyof typeof GLOBAL_ROLE]
>;

export type UserRoles = {
  isExternalOperator: boolean;
  isExternalApplicant: boolean;
  isExternalWithoutRole: boolean;
  isInternalServiceAdministrator: boolean;
  isInternalMonitoring: boolean;
  isInternalNtpChief: boolean;
  isInternalApparatusNg: boolean;
  isInternalWithoutRole: boolean;
  isInternalOperator: boolean;
  isInternalNtpResponsible: boolean;
  isInternalRelatedDepartments: boolean;
  isInternalNgSecretary: boolean;
  isInternalAgreeing: boolean;
};

const DEFAULT_ROLES: UserRoles = {
  isExternalOperator: false,
  isExternalApplicant: false,
  isExternalWithoutRole: false,
  isInternalServiceAdministrator: false,
  isInternalMonitoring: false,
  isInternalNtpChief: false,
  isInternalApparatusNg: false,
  isInternalWithoutRole: false,
  isInternalOperator: false,
  isInternalNtpResponsible: false,
  isInternalRelatedDepartments: false,
  isInternalNgSecretary: false,
  isInternalAgreeing: false,
};

const getUserRolesFromToken = (token?: string): UserRoles => {
  if (!token) {
    return DEFAULT_ROLES;
  }

  try {
    const info = jwtDecode<{ "global-role"?: string[] }>(token);
    const globalRoles = info?.["global-role"] ?? [];

    return {
      isExternalOperator: globalRoles.includes(GLOBAL_ROLE.EXTERNAL_OPERATOR),
      isExternalApplicant: globalRoles.includes(GLOBAL_ROLE.EXTERNAL_APPLICANT),
      isExternalWithoutRole: globalRoles.includes(GLOBAL_ROLE.EXTERNAL_WITHOUT_ROLE),
      isInternalServiceAdministrator: globalRoles.includes(GLOBAL_ROLE.INTERNAL_SERVICE_ADMINISTRATOR),
      isInternalMonitoring: globalRoles.includes(GLOBAL_ROLE.INTERNAL_MONITORING),
      isInternalNtpChief: globalRoles.includes(GLOBAL_ROLE.INTERNAL_NTP_CHIEF),
      isInternalApparatusNg: globalRoles.includes(GLOBAL_ROLE.INTERNAL_APPARATUS_NG),
      isInternalWithoutRole: globalRoles.includes(GLOBAL_ROLE.INTERNAL_WITHOUT_ROLE),
      isInternalOperator: globalRoles.includes(GLOBAL_ROLE.INTERNAL_OPERATOR),
      isInternalNtpResponsible: globalRoles.includes(GLOBAL_ROLE.INTERNAL_NTP_RESPONSIBLE),
      isInternalRelatedDepartments: globalRoles.includes(GLOBAL_ROLE.INTERNAL_RELATED_DEPARTMENTS),
      isInternalNgSecretary: globalRoles.includes(GLOBAL_ROLE.INTERNAL_NG_SECRETARY),
      isInternalAgreeing: globalRoles.includes(GLOBAL_ROLE.INTERNAL_AGREEING),
    };
  } catch {
    return DEFAULT_ROLES;
  }
};

/** Роли текущего пользователя без React-хука — для `beforeLoad` роутера. */
export const getUserRoles = (): UserRoles => getUserRolesFromToken(getUser()?.access_token);

export const useUserRoles = (): UserRoles => {
  const user = getUser();

  return useMemo(() => getUserRolesFromToken(user?.access_token), [user]);
};
